<script setup lang="ts">
/**
 * Text-Channel-Ansicht (#channel): Header mit Hash + Channelname,
 * Nachrichtenliste (Slot), Eingabezeile. Die Bühne für das Ticket-Panel
 * (Bot-Embed mit „Ticket erstellen"-Button).
 */
withDefaults(defineProps<{
  channel: string
  topic?: string
  placeholder?: string
}>(), {
  topic: undefined,
  placeholder: 'Message #channel'
})
</script>

<template>
  <div class="dc-channel">
    <div class="dc-channel-header">
      <span class="dc-channel-hash" aria-hidden="true">#</span>
      <span class="dc-channel-name">{{ channel }}</span>
      <template v-if="topic">
        <span class="dc-channel-divider" aria-hidden="true" />
        <span class="dc-channel-topic">{{ topic }}</span>
      </template>
      <div class="dc-channel-icons" aria-hidden="true">
        <UIcon name="i-lucide-bell" class="size-[17px]" />
        <UIcon name="i-lucide-pin" class="size-[17px]" />
        <UIcon name="i-lucide-users" class="size-[18px]" />
        <UIcon name="i-lucide-search" class="size-[17px]" />
      </div>
    </div>

    <div class="dc-channel-messages">
      <div class="dc-scroll">
        <slot />
      </div>
    </div>

    <div class="dc-channel-footer" aria-hidden="true">
      <div class="dc-channel-input">
        <UIcon name="i-lucide-circle-plus" class="dc-channel-input-plus size-[22px]" aria-hidden="true" />
        <span class="dc-channel-input-placeholder">{{ placeholder }}</span>
        <div class="dc-channel-input-icons" aria-hidden="true">
          <UIcon name="i-lucide-gift" class="size-[20px]" />
          <UIcon name="i-lucide-image" class="size-[20px]" />
          <UIcon name="i-lucide-smile" class="size-[20px]" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dc-channel {
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--dc-bg-chat);
  border: 1px solid var(--dc-hairline);
  border-radius: var(--radius-md);
  overflow: hidden;
  font-family: var(--font-discord);
  box-shadow: var(--shadow-float);
}

.dc-channel-header {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 16px;
  border-bottom: 1px solid var(--dc-hairline);
  flex-shrink: 0;
}

.dc-channel-hash {
  font-size: 1.4rem;
  font-weight: 400;
  color: var(--dc-ink-channel);
  line-height: 1;
}

.dc-channel-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--dc-ink-header);
}

.dc-channel-divider {
  width: 1px;
  height: 20px;
  background: var(--dc-border-card);
  margin: 0 4px;
}

.dc-channel-topic {
  font-size: 0.82rem;
  color: var(--dc-ink-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.dc-channel-icons {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 14px;
  color: var(--dc-ink-channel);
  flex-shrink: 0;
}

.dc-channel-messages {
  flex: 1;
  padding: 12px 0 26px; /* großzügige Luft zwischen letzter Nachricht und Input */
  overflow: hidden;
  position: relative;
}

/* .dc-scroll: kein dauerhaftes will-change — GSAP promotet die Liste nur
   während des Fake-Scroll-Tweens (force3D) */

.dc-channel-footer {
  padding: 0 16px 12px;
  flex-shrink: 0;
}

.dc-channel-input {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--dc-bg-input);
  border: 1px solid var(--dc-border-card);
  border-radius: 24px;
  padding: 10px 16px;
}

.dc-channel-input-plus {
  color: var(--dc-ink-channel);
  flex-shrink: 0;
}

.dc-channel-input-placeholder {
  flex: 1;
  font-size: 0.95rem;
  color: var(--dc-ink-channel);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dc-channel-input-icons {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--dc-ink-channel);
}
</style>
